import React from 'react';
import { GetServerSideProps } from 'next';
import {
  getLocaleProps, WithLocale, getAuthenticationProps, WithAuthentication,
  LocaleProps, AuthenticationProps, PageWrapper, PricingPage, ErrorOrPage,
} from '@components/.';
import { Profile, ProfileController, ProfileSubscriptionsPrice, send, State, SubscriptionController, withCookie } from '@oward/openapi';
import { hasActiveSubscription, oniOSWebview } from '@utils/utils';

interface PageProps {
  prices?: ProfileSubscriptionsPrice[];
  errorMsg?: string;
  notLogged?: boolean;
}

export const getServerSideProps: GetServerSideProps = async context => {
  let pageProps: PageProps;
  const locale = context.params.lang as string;
  const localeProps = await getLocaleProps(locale);
  const authenticationProps = await getAuthenticationProps(context);

  // If there's a user connected and he has exactly one Live Profile with no active subscription,
  // redirect to subscription page of this Profile
  if (authenticationProps.currentUser) {
    try {
      let cookie = context.req.headers.cookie;
      let profiles: Profile[];
      profiles = await withCookie(cookie)(ProfileController.findForUser(locale));
      let liveProfiles = profiles?.filter(profile => profile.state === State.LIVE);
      if (
        liveProfiles?.length === 1 &&
        !hasActiveSubscription(liveProfiles[0])
      ) {
        return {
          redirect: {
            permanent: false,
            destination: `/${locale}/subscriptions/${liveProfiles[0].id}`
          }
        }
      }
    }
    catch (error) {
      // Do nothing and display classic Pricing Page
    }
  }

  try {
    pageProps = {
      prices: await send(SubscriptionController.findProfileSubscriptionsPrices())
    }
  }
  catch (error) {
    pageProps = { errorMsg: (error as Error)?.message }
  }

  return {
    props: {
      ...pageProps,
      ...authenticationProps,
      ...localeProps
    }
  }
}

const Page: React.FC<PageProps> = props => {
  return (
    <PageWrapper>
      {
        oniOSWebview() ?
          <ErrorOrPage errorMsg={'Not available on iOS application'} />
          :
          <ErrorOrPage errorMsg={props.errorMsg}>
            <PricingPage prices={props.prices} />
          </ErrorOrPage>
      }
    </PageWrapper>
  );
}

const CompletePage: React.FC<PageProps & LocaleProps & AuthenticationProps> = props => {
  return (
    <WithLocale {...props}>
      <WithAuthentication {...props}>
        <Page {...props} />
      </WithAuthentication>
    </WithLocale>
  );
}

export default CompletePage;
